import React from 'react'
import PropTypes from 'prop-types'
import { Route, Switch, useRouteMatch, Redirect } from 'react-router-dom'
import { connect } from 'react-redux'
import * as Views from './views'
import Layout from './Layout'
import MemberApplicationLayout from './MemberApplicationLayout'
import {
  ApplicationPrefillProvider,
  NewMemberApplicationAccountProvider,
  QuestionnaireRedirect,
} from './components'
import {
  MarketingCodeProvider,
  MembershipUpdater,
  ProductPricesGuard,
  StripePaymentIntentReset,
} from 'member-portal/components'
import { selectors as memberSelectors } from 'member-reducer'
import { PATH, makePath, makeMemberApplicationApplyPath } from 'config'
import { legacyPathRedirect } from 'utils'

const propTypes = {
  checkoutComplete: PropTypes.bool.isRequired,
}

function Routes({ checkoutComplete }) {
  const { path, url } = useRouteMatch()
  const confirmationPath = makePath(url, PATH.CONFIRM_AND_LOGIN)

  return (
    <StripePaymentIntentReset>
      <QuestionnaireRedirect>
        <MarketingCodeProvider>
          <Layout>
            <Switch>
              {/* Hoisted out of the inner providers, mirroring Renewal. */}
              <Route path={confirmationPath}>
                <Views.FinalConfirmationAndLogin />
              </Route>

              {/* Redirect here so the inner providers don't briefly render
                  after a successful purchase. */}
              {checkoutComplete && <Redirect push to={confirmationPath} />}

              <Route>
                <ApplicationPrefillProvider>
                  <Switch>
                    {/* Pre-application Routes */}
                    <Route exact path={makePath(path, PATH.ROOT)}>
                      <Views.NewOrRenew />
                    </Route>
                    <Route
                      path={makePath(path, legacyPathRedirect.PATH.NEW_MEMBER_QUESTIONNAIRE)}
                      render={({ location }) => (
                        <Redirect
                          to={{
                            ...location,
                            pathname: legacyPathRedirect.resolvePathname(location.pathname),
                          }}
                        />
                      )}
                    />
                    <Route path={makePath(path, PATH.NEW_MEMBER_QUESTIONNAIRE)}>
                      <Views.NewMemberQuestionnaire />
                    </Route>
                    <Route path={makePath(path, PATH.MEMBERSHIP_TYPE_SELECTED)}>
                      <ProductPricesGuard>
                        <Views.NewMemberSelectedMembershipType />
                      </ProductPricesGuard>
                    </Route>
                    <Route path={makePath(path, PATH.MEMBERSHIP_TYPE_ALL)}>
                      <Views.AllMembershipTypes />
                    </Route>
                    {/* Boat Routes */}
                    <Route
                      exact
                      path={makePath(path, PATH.BOATS, PATH.HOME_PORT_TYPE)}
                    >
                      <Views.NewMemberHomePortType />
                    </Route>
                    <Route
                      exact
                      path={makePath(path, PATH.BOATS, PATH.HOME_PORT_ADDRESS)}
                    >
                      <Views.NewMemberSearchAddress />
                    </Route>
                    <Route
                      exact
                      path={makePath(path, PATH.BOATS, PATH.HOME_PORT_LOCATION)}
                    >
                      <Views.NewMemberLocatePort />
                    </Route>
                    {/* Application Routes */}
                    <Route
                      exact
                      path={makeMemberApplicationApplyPath(PATH.ROOT)}
                    >
                      <Redirect
                        to={makeMemberApplicationApplyPath(PATH.CONTACT)}
                      />
                    </Route>
                    <Route>
                      <NewMemberApplicationAccountProvider>
                        <MembershipUpdater>
                          <MemberApplicationLayout>
                            <Switch>
                              <Route
                                exact
                                path={makeMemberApplicationApplyPath(
                                  PATH.CONTACT,
                                )}
                              >
                                <Views.MemberContactInfo />
                              </Route>
                              <Route
                                exact
                                path={makeMemberApplicationApplyPath(
                                  PATH.CONTACT_2,
                                )}
                              >
                                <Views.MemberContactInfo />
                              </Route>
                              <Route
                                exact
                                path={makeMemberApplicationApplyPath(PATH.BOATS)}
                              >
                                <Views.NewMemberBoatSelection />
                              </Route>
                              <Route
                                exact
                                path={makeMemberApplicationApplyPath(
                                  PATH.TERMS_AND_CONDITIONS,
                                )}
                              >
                                <Views.NewMemberTermsAndConditions />
                              </Route>
                              <Route
                                exact
                                path={makeMemberApplicationApplyPath(
                                  PATH.PAYMENT,
                                )}
                              >
                                <Views.NewMemberPayment />
                              </Route>
                            </Switch>
                          </MemberApplicationLayout>
                        </MembershipUpdater>
                      </NewMemberApplicationAccountProvider>
                    </Route>
                  </Switch>
                </ApplicationPrefillProvider>
              </Route>
            </Switch>
          </Layout>
        </MarketingCodeProvider>
      </QuestionnaireRedirect>
    </StripePaymentIntentReset>
  )
}

Routes.propTypes = propTypes

function mapStateToProps(state) {
  return {
    checkoutComplete: memberSelectors.checkoutComplete(state),
  }
}

export default connect(mapStateToProps)(Routes)
